import {
  SOURCE_ENTRIES_PAGE_DESCRIPTION,
  SOURCE_ENTRIES_PAGE_HEADING,
} from "@/config/general";
import {
  deleteSourceEntry,
  downloadSourceGroup,
} from "@/services/sourceService";
import {
  ArrowLeft,
  BarChart4Icon,
  Download,
  Pencil,
  Plus,
  Trash,
  Upload,
} from "lucide-react";
import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { DashboardMainCard } from "@/components/cards/DashboardMainCard";
import { ConfirmDialog } from "@/components/dialog/ConfirmDialog";
import { SourceEntryModal } from "@/components/dialog/SourceEntryModal";
import { SourceUploadModal } from "@/components/dialog/SourceUploadModal";
import DataTable from "@/components/table/CustomDataTable";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { downloadCSV, getNameBySymbol, generateCellIcons } from "@/lib/utils";
import useSourcesStore from "@/stores/sources.store";
import { NetworksData, Source } from "@/types/sources.type";
import { validateAndCleanFile } from "@/zod/source.zod";
import { colsMap, filtersMap } from "@/components/misc/TableConfig";

function SourceEntries() {
  const { sourceGroupId } = useParams<{ sourceGroupId: string }>();
  const [loading, clearSourceEntries] = useSourcesStore((state) => [
    state.loading,
    state.clearSourceEntries,
  ]);
  const navigate = useNavigate();
  const { toast } = useToast();

  const setSelectedSourceGroup = useSourcesStore(
    (state) => state.setSelectedSourceGroup
  );

  useEffect(() => {
    setSelectedSourceGroup(sourceGroupId || "");
    return () => {
      clearSourceEntries();
    };
  }, [sourceGroupId, setSelectedSourceGroup, clearSourceEntries]);

  const [networks, fetchAllSourceGroups, getSourceEntriesForSourceGroup] =
    useSourcesStore((state) => [
      state.networks,
      state.fetchAllSourceGroups,
      state.fetchSourceEntriesForSelectedGroup,
    ]);

  const { selectedSourceGroup, setSelectedSource } = useSourcesStore();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(100);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalRows, setTotalRows] = useState<number>(1);
  const [sourceEntries, setSourceEntries] = useState<Source[]>([]);

  const [isEditOverlayOpen, setIsEditOverlayOpen] = useState(false);
  const [deleteEntryDialogOpen, setDeleteEntryDialogOpen] = useState(false);
  const [editSourceEntryId, setEditSourceEntryId] = useState<string | null>(
    null
  );
  const [editSourceGroupId, setEditSourceGroupId] = useState<string | null>(
    null
  );

  const [isUploadOverlayOpen, setIsUploadOverlayOpen] = useState(false);

  const fetchSourceEntriesForGroup = useCallback(
    async (sourceGroupId: string, page: number, limit: number) => {
      const response = await getSourceEntriesForSourceGroup(
        sourceGroupId,
        limit,
        page
      );
      if (response && response.data && response.data.length > 0) {
        setSourceEntries(response.data);
        setTotalPages(response.pages);
        setTotalRows(response.max);
        if (response.limit) setPageSize(response.limit);
      }
    },
    [getSourceEntriesForSourceGroup]
  );

  const cols = selectedSourceGroup
    ? colsMap[selectedSourceGroup.groupType]
    : [];

  const filter = selectedSourceGroup
    ? filtersMap[selectedSourceGroup.groupType]
    : undefined;

  const uploadComplete = async () => {
    toast({
      title: "Success",
      description: "Source uploaded successfully",
    });
    setTimeout(async () => {
      await fetchSourceEntriesForGroup(sourceGroupId!, currentPage, pageSize);
    }, 2000);
  };

  useEffect(() => {
    if (sourceGroupId) {
      fetchSourceEntriesForGroup(sourceGroupId, currentPage, pageSize);
    }
  }, [sourceGroupId, pageSize, fetchSourceEntriesForGroup, currentPage]);

  useEffect(() => {
    if (!isEditOverlayOpen && sourceGroupId) {
      fetchSourceEntriesForGroup(sourceGroupId, currentPage, pageSize);
    }
  }, [
    isEditOverlayOpen,
    sourceGroupId,
    currentPage,
    pageSize,
    fetchSourceEntriesForGroup,
  ]);

  const downloadSource = async () => {
    if (!sourceGroupId) return;
    const response = await downloadSourceGroup(sourceGroupId);
    const cleanCSV = validateAndCleanFile(
      response.data.data,
      selectedSourceGroup?.groupType || ""
    );
    downloadCSV(cleanCSV, `${selectedSourceGroup?.sourceName}.csv`);
  };

  const runReport = async () => {
    navigate(`/reports/run/${sourceGroupId}`);
  };

  const deleteSourceEntryFn = async () => {
    if (editSourceEntryId) {
      await deleteSourceEntry(editSourceEntryId);
    }
    await fetchAllSourceGroups();
    if (editSourceGroupId) {
      setSelectedSourceGroup(editSourceGroupId);
      await fetchSourceEntriesForGroup(
        editSourceGroupId,
        currentPage,
        pageSize
      );
    }
  };
  const selectedGroupType =
    selectedSourceGroup?.groupType as keyof NetworksData;
  const cellIcons = generateCellIcons(networks, selectedGroupType);

  const handlePageChange = (page: number) => {
    setCurrentPage(!isNaN(page) ? page : 2);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(!isNaN(size) && size > 0 ? size : 100);
  };

  return (
    <div className="">
      <ConfirmDialog
        title="Delete Entry"
        description="Are you sure you want to delete this source entry?"
        open={deleteEntryDialogOpen}
        setOpen={setDeleteEntryDialogOpen}
        severity="error"
        onConfirm={async () => {
          await deleteSourceEntryFn();
          toast({
            title: "Entry deleted",
            description: "Source entry was deleted successfully",
          });
        }}
      />
      <SourceEntryModal
        isEditDialogOpen={isEditOverlayOpen}
        setIsEditDialogOpen={setIsEditOverlayOpen}
        sourceEntryId={editSourceEntryId}
      />

      <div className="flex flex-wrap flex-col p-4 mb-2 bg-white shadow rounded-lg max-w-6xl w-full mx-auto dark:bg-gray-800">
        <div>
          <span className="text-xl font-semibold text-gray-800">
            Source: {selectedSourceGroup?.sourceName}
          </span>
          <span className="text-gray-600 block text-sm">
            ID: {selectedSourceGroup?.sourceGroupId}
          </span>
        </div>
        <div className="flex items-center space-x-4 m-2">
          <span className="text-md font-semibold">Source Type:</span>
          <span className="text-gray-700 dark:text-gray-300 block text-sm border rounded-full border-gray-300 px-4 py-2">
            {getNameBySymbol(selectedSourceGroup?.groupType || "")}
          </span>
        </div>

        <div className="col-span-2 pl-2 flex ">
          <Button
            className="w-full bg-primary"
            variant="outline"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Sources
          </Button>
          <Button
            className="w-full ml-2 bg-primary"
            variant="outline"
            onClick={downloadSource}
            disabled={selectedSourceGroup?.count === 0}
          >
            <Download className="h-4 w-4 mr-2" />
            Download CSV
          </Button>
          <Button
            className="w-full ml-2 bg-primary border"
            variant="outline"
            onClick={runReport}
            disabled={selectedSourceGroup?.count === 0}
          >
            <BarChart4Icon className="h-4 w-4 mr-2" />
            Run Report
          </Button>
        </div>
      </div>
      <SourceEntryModal
        isEditDialogOpen={isEditOverlayOpen}
        setIsEditDialogOpen={setIsEditOverlayOpen}
        sourceEntryId={editSourceEntryId}
        sourceType={selectedSourceGroup?.groupType}
      />
      <SourceUploadModal
        isOpen={isUploadOverlayOpen}
        setIsOpen={setIsUploadOverlayOpen}
        sourceGroupId={sourceGroupId!}
        onSuccess={uploadComplete}
      />
      <DashboardMainCard
        header={SOURCE_ENTRIES_PAGE_HEADING}
        description={SOURCE_ENTRIES_PAGE_DESCRIPTION}
      >
        <DataTable
          columns={cols}
          columnIcons={[
            { field: "entry.network", icons: cellIcons },
            { field: "entry.asset", icons: cellIcons },
          ]}
          data={sourceEntries}
          loading={loading}
          globalFilter={filter}
          actionButtons={[
            {
              onClick: () => setIsUploadOverlayOpen(true),
              text: "Import CSV",
              icon: Upload,
            },
            {
              onClick: () => {
                setIsEditOverlayOpen(true);
                setEditSourceEntryId(null);
              },
              text: "Add Entry",
              icon: Plus,
            },
          ]}
          pagination={{
            currentPage,
            pageSize,
            totalPages,
            totalRows,
            onPageChange: handlePageChange,
            onPageSizeChange: handlePageSizeChange,
          }}
          rowActions={[
            {
              onClick: (rowData: Source) => {
                setSelectedSource(rowData);
                setIsEditOverlayOpen(true);
                setEditSourceEntryId(rowData.sourceId);
              },
              text: "Edit",
              icon: Pencil,
            },
            {
              onClick: async (rowData: Source) => {
                setDeleteEntryDialogOpen(true);
                setEditSourceEntryId(rowData.sourceId);
                setEditSourceGroupId(rowData.sourceGroupId);
              },
              text: "Delete",
              icon: Trash,
              variant: "danger",
            },
          ]}
        />
      </DashboardMainCard>
    </div>
  );
}

export default SourceEntries;
